import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Grid,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TablePagination,
  Chip,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import SnackbarWrapper from "components/Snackbar";
import LoadingScreen from "components/Loading";
import AssignCompensationDialog from "components/AssignCompensationDialog";
import {
  getEmployeesBasicInfo,
  getEmployeesPaging,
  getJobs,
  getPayrollStructures,
  getCompensationHistory,
  correctCompensation,
} from "services/api";

const CompensationHistoryView = ({ canEdit }) => {
  const { t } = useTranslation();
  const snackbarRef = useRef();

  const [loading, setLoading] = useState(true);

  // Full lightweight list - powers the dialog's Employee/Approver pickers
  // and lets us resolve ids (approvedBy) to names in the timeline.
  const [pickerEmployees, setPickerEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [structures, setStructures] = useState([]);

  const [pagedEmployees, setPagedEmployees] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(false);

  // Filters
  const [jobFilter, setJobFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [correctDialogOpen, setCorrectDialogOpen] = useState(false);
  const [recordBeingCorrected, setRecordBeingCorrected] = useState(null);

  useEffect(() => {
    const loadStatic = async () => {
      setLoading(true);
      const [pickerRes, jobsRes, structuresRes] = await Promise.all([
        getEmployeesBasicInfo(),
        getJobs(),
        getPayrollStructures(),
      ]);
      if (pickerRes !== "error") setPickerEmployees(pickerRes);
      if (jobsRes !== "error") setJobs(jobsRes);
      if (structuresRes !== "error") setStructures(structuresRes);
      setLoading(false);
    };
    loadStatic();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [jobFilter, debouncedSearch]);

  const fetchPage = useCallback(async () => {
    setTableLoading(true);
    const res = await getEmployeesPaging(
      page,
      rowsPerPage,
      jobFilter?.jobId || undefined,
      undefined,
      undefined,
      debouncedSearch || undefined
    );

    if (res === "error") {
      setPagedEmployees([]);
      setTotalElements(0);
    } else {
      // Defensive: handle both a standard Spring Page ({ content, totalElements })
      // and a plain array.
      const content = Array.isArray(res) ? res : res?.content || [];
      const total = Array.isArray(res) ? res.length : (res?.totalElements ?? content.length);
      setPagedEmployees(content);
      setTotalElements(total);
    }
    setTableLoading(false);
  }, [page, rowsPerPage, jobFilter, debouncedSearch]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const structureName = (structureId) => {
    const match = structures.find((s) => s.id === structureId);
    return match ? match.name : `#${structureId}`;
  };

  const employeeName = (employeeId) => {
    const match = pickerEmployees.find((e) => e.empId === employeeId);
    return match ? match.empName : `#${employeeId}`;
  };

  const loadHistory = useCallback(async (emp) => {
    setLoadingHistory(true);
    const res = await getCompensationHistory(emp.empId);
    setHistory(res !== "error" && Array.isArray(res) ? res : []);
    setLoadingHistory(false);
  }, []);

  const handleSelectEmployee = async (emp) => {
    setSelectedEmployee(emp);
    setHistory([]);
    await loadHistory(emp);
  };

  const handleOpenCorrect = (record) => {
    setRecordBeingCorrected(record);
    setCorrectDialogOpen(true);
  };

  const handleSubmitCorrection = async (form) => {
    const { id, ...compensation } = form;
    const res = await correctCompensation(id, compensation);

    if (res !== "error" && typeof res !== "string") {
      snackbarRef.current?.showSnackbar(t("admin.payroll.employees.correctSuccess"), "success");
      setCorrectDialogOpen(false);
      if (selectedEmployee) {
        loadHistory(selectedEmployee);
      }
    } else {
      snackbarRef.current?.showSnackbar(
        typeof res === "string" ? res : t("admin.payroll.employees.correctError"),
        "error"
      );
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={5}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(job) => job.jobName || ""}
              isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
              value={jobFilter}
              onChange={(event, newValue) => setJobFilter(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t("admin.payroll.employees.filterByJob")} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              label={t("admin.payroll.employees.searchEmployee")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={selectedEmployee ? 6 : 12}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {tableLoading ? (
              <LoadingScreen />
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.employee")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          {t("admin.payroll.employees.job")}
                        </TableCell>
                      </TableRow>
                      {pagedEmployees.map((emp) => (
                        <TableRow
                          key={emp.empId}
                          hover
                          selected={selectedEmployee?.empId === emp.empId}
                          onClick={() => handleSelectEmployee(emp)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{emp.empName}</TableCell>
                          <TableCell>{emp.jobName || t("admin.payroll.employees.noJob")}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={totalElements}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </Paper>
        </Grid>

        {selectedEmployee && (
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="subtitle2">{selectedEmployee.empName}</MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                {selectedEmployee.jobName || t("admin.payroll.employees.noJob")}
              </MDTypography>

              {loadingHistory ? (
                <LoadingScreen />
              ) : history.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  {t("admin.payroll.employees.notOnboarded")}
                </MDTypography>
              ) : (
                <MDBox>
                  {history.map((record, idx) => {
                    const isActive = record.status === "active";
                    return (
                      <MDBox key={record.id}>
                        <MDBox
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            py: 1.5,
                          }}
                        >
                          <MDBox>
                            <MDBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <MDTypography variant="body2" sx={{ fontWeight: "bold" }}>
                                {structureName(record.payrollStructureId)}
                              </MDTypography>
                              <Chip
                                size="small"
                                label={t(
                                  isActive
                                    ? "admin.payroll.employees.active"
                                    : "admin.payroll.employees.superseded"
                                )}
                                color={isActive ? "success" : "default"}
                              />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={t(
                                  `admin.payroll.assignCompensation.reasons.${record.reason}`
                                )}
                              />
                            </MDBox>
                            <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                              {record.effectiveTo
                                ? t("admin.payroll.employees.dateRange", {
                                    from: record.effectiveFrom,
                                    to: record.effectiveTo,
                                  })
                                : t("admin.payroll.employees.since", {
                                    date: record.effectiveFrom,
                                  })}{" "}
                              · {record.currency}
                            </MDTypography>
                            {record.approvedBy && (
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ display: "block" }}
                              >
                                {t("admin.payroll.employees.approvedByLabel", {
                                  name: employeeName(record.approvedBy),
                                })}
                              </MDTypography>
                            )}
                          </MDBox>
                          {canEdit && (
                            <MDButton
                              size="small"
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleOpenCorrect(record)}
                            >
                              {t("admin.payroll.employees.correct")}
                            </MDButton>
                          )}
                        </MDBox>
                        {idx < history.length - 1 && <Divider />}
                      </MDBox>
                    );
                  })}
                </MDBox>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      <AssignCompensationDialog
        open={correctDialogOpen}
        onClose={() => setCorrectDialogOpen(false)}
        onSubmit={handleSubmitCorrection}
        employees={pickerEmployees}
        payrollStructures={structures}
        approvers={pickerEmployees}
        mode="correct"
        correctingRecord={recordBeingCorrected}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};

CompensationHistoryView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default CompensationHistoryView;
